// Contrato y documentos de una vivienda.
//
// Este panel es la palanca del portal del inquilino: mientras no exista un
// contrato con su client_id, quien entre en /cuenta/mi-vivienda no verá nada.
// Dar de alta el contrato es lo que le abre la puerta.
import { useState } from 'react';
import { FileText, Trash2, Upload } from 'lucide-react';
import {
  useContratosDeVivienda,
  useDocumentos,
  useGuardarContrato,
  useSubirDocumento,
  useBorrarDocumento,
  type Contrato,
} from '../api/portal';
import { useClients } from '../api/clients';

const TIPOS_DOC = ['contrato', 'anexo', 'certificado', 'factura', 'otro'];

export default function ContratoYDocumentos({ propertyId }: { propertyId: string }) {
  const { data: contratos } = useContratosDeVivienda(propertyId);
  const vigente = contratos?.find((c) => c.estado === 'vigente') ?? null;
  const [editando, setEditando] = useState(false);

  return (
    <section className="mt-6 border-t border-deep/10 pt-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold text-deep">Contrato de alquiler</h3>
        {!editando && (
          <button
            onClick={() => setEditando(true)}
            className="rounded-full border border-deep/15 px-4 py-1.5 text-xs font-bold text-deep transition hover:border-brand hover:text-brand"
          >
            {vigente ? 'Editar contrato' : 'Dar de alta un contrato'}
          </button>
        )}
      </div>

      {editando ? (
        <FormContrato
          propertyId={propertyId}
          contrato={vigente}
          onDone={() => setEditando(false)}
        />
      ) : !vigente ? (
        <p className="mt-2 text-sm text-ink/55">
          Sin contrato en vigor. Hasta que exista, el inquilino no ve nada en su
          área privada.
        </p>
      ) : (
        <>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <Dato k="Inquilino" v={<NombreCliente id={vigente.tenant_client_id} />} />
            <Dato k="Renta" v={vigente.renta ? `${vigente.renta} €/mes` : '—'} />
            <Dato k="Desde" v={vigente.fecha_inicio} />
            <Dato k="Hasta" v={vigente.fecha_fin || '—'} />
          </dl>
          <Documentos refId={vigente.contract_id} />
        </>
      )}
    </section>
  );
}

function NombreCliente({ id }: { id: string }) {
  const { data } = useClients();
  return <>{data?.find((c) => c.client_id === id)?.nombre ?? id}</>;
}

function FormContrato({
  propertyId,
  contrato,
  onDone,
}: {
  propertyId: string;
  contrato: Contrato | null;
  onDone: () => void;
}) {
  const guardar = useGuardarContrato();
  const { data: clientes } = useClients();
  const [f, setF] = useState({
    tenant_client_id: contrato?.tenant_client_id ?? '',
    fecha_inicio: contrato?.fecha_inicio ?? '',
    fecha_fin: contrato?.fecha_fin ?? '',
    renta: String(contrato?.renta ?? ''),
    fianza: String(contrato?.fianza ?? ''),
    estado: contrato?.estado ?? 'vigente',
  });

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    guardar.mutate(
      {
        ...(contrato ? { contract_id: contrato.contract_id } : { property_id: propertyId }),
        ...f,
        renta: Number(f.renta || 0),
        fianza: Number(f.fianza || 0),
        estado: f.estado as Contrato['estado'],
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={enviar} className="mt-3 space-y-3 rounded-xl bg-soft p-4">
      {!contrato && (
        <label className="block text-sm">
          <span className="font-bold text-ink/60">Inquilino</span>
          <select
            required
            value={f.tenant_client_id}
            onChange={(e) => setF({ ...f, tenant_client_id: e.target.value })}
            className="mt-1 w-full rounded-xl border border-deep/15 px-3 py-2 outline-none focus:border-brand"
          >
            <option value="">Elige un cliente…</option>
            {clientes?.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.nombre} {c.email ? `· ${c.email}` : ''}
                {c.user_id ? ' · tiene cuenta' : ''}
              </option>
            ))}
          </select>
          {/* Aviso útil: si el cliente no tiene cuenta todavía, el contrato se
              crea igual y él lo verá en cuanto se registre con ese email. */}
          <span className="mt-1 block text-xs text-ink/45">
            Si aún no tiene cuenta, el contrato se guarda igual y lo verá al
            registrarse con ese email.
          </span>
        </label>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Campo
          etiqueta="Desde"
          type="date"
          value={f.fecha_inicio}
          onChange={(v) => setF({ ...f, fecha_inicio: v })}
          required
        />
        <Campo
          etiqueta="Hasta"
          type="date"
          value={f.fecha_fin}
          onChange={(v) => setF({ ...f, fecha_fin: v })}
        />
        <Campo
          etiqueta="Renta (€/mes)"
          type="number"
          value={f.renta}
          onChange={(v) => setF({ ...f, renta: v })}
        />
        <Campo
          etiqueta="Fianza (€)"
          type="number"
          value={f.fianza}
          onChange={(v) => setF({ ...f, fianza: v })}
        />
      </div>

      {contrato && (
        <label className="block text-sm">
          <span className="font-bold text-ink/60">Estado</span>
          <select
            value={f.estado}
            onChange={(e) => setF({ ...f, estado: e.target.value as Contrato['estado'] })}
            className="mt-1 w-full rounded-xl border border-deep/15 px-3 py-2 outline-none focus:border-brand"
          >
            <option value="vigente">Vigente</option>
            <option value="finalizado">Finalizado</option>
          </select>
        </label>
      )}

      {guardar.isError && (
        <p className="text-sm font-semibold text-red-600">
          {(guardar.error as Error)?.message}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={guardar.isPending}
          className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-50"
        >
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-full border border-deep/15 px-5 py-2 text-sm font-bold text-deep"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function Documentos({ refId }: { refId: string }) {
  const { data: docs } = useDocumentos(refId);
  const subir = useSubirDocumento(refId);
  const borrar = useBorrarDocumento(refId);
  const [tipo, setTipo] = useState('contrato');

  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-sm font-bold text-deep">Documentos del inquilino</h4>
        <div className="flex items-center gap-2">
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="rounded-full border border-deep/15 px-3 py-1.5 text-xs font-bold outline-none focus:border-brand"
          >
            {TIPOS_DOC.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <label className="flex cursor-pointer items-center gap-1.5 rounded-full bg-brand px-4 py-1.5 text-xs font-bold text-white transition hover:bg-deep">
            <Upload size={13} /> {subir.isPending ? 'Subiendo…' : 'Subir'}
            <input
              type="file"
              className="hidden"
              disabled={subir.isPending}
              onChange={(e) =>
                e.target.files?.[0] && subir.mutate({ file: e.target.files[0], tipo })
              }
            />
          </label>
        </div>
      </div>

      {!docs?.length ? (
        <p className="mt-2 text-sm text-ink/50">
          Ningún documento todavía. Lo que suba aquí lo puede descargar el
          inquilino desde su portal.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-deep/5">
          {docs.map((d) => (
            <li key={d.document_id} className="flex items-center gap-3 py-2 text-sm">
              <FileText size={15} className="shrink-0 text-brand" />
              <span className="min-w-0 flex-1 truncate font-semibold text-deep">
                {d.nombre}
              </span>
              <span className="shrink-0 text-xs uppercase tracking-wide text-ink/40">
                {d.tipo}
              </span>
              <button
                onClick={() => borrar.mutate(d.document_id)}
                aria-label={`Borrar ${d.nombre}`}
                className="shrink-0 text-ink/35 transition hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Dato({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{k}</dt>
      <dd className="font-semibold text-deep">{v}</dd>
    </div>
  );
}

function Campo({
  etiqueta,
  value,
  onChange,
  ...props
}: {
  etiqueta: string;
  value: string;
  // Se queda con el valor y no con el evento; por eso hay que sacar el onChange
  // nativo del tipo, o TypeScript intenta casar las dos firmas.
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>) {
  return (
    <label className="block">
      <span className="font-bold text-ink/60">{etiqueta}</span>
      <input
        {...props}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-deep/15 px-3 py-2 outline-none focus:border-brand"
      />
    </label>
  );
}
